import React, { useState } from 'react';
import { useApi, useMutation } from '../hooks/useApi';
import { upgradeRequestAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  EyeIcon, 
  DocumentArrowDownIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import AcceptanceDocument from './AcceptanceDocument';
import { formatDate } from '../utils/dateUtils';

const RequestList = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDocument, setShowDocument] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetails, setShowDetails] = useState(null);

  const { data, loading, refetch } = useApi(
    () => upgradeRequestAPI.getAll({
      search: search || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      page: currentPage,
      limit: 10
    }),
    [search, statusFilter, currentPage]
  );

  const { mutate: deleteRequest, loading: deleteLoading } = useMutation(
    upgradeRequestAPI.delete,
    {
      onSuccess: () => {
        refetch();
      }
    }
  );

  const handleViewDocument = async (request) => {
    if (request.status !== 'approved') {
      return;
    }

    try {
      const response = await upgradeRequestAPI.getAcceptanceDocument(request.id);
      setSelectedRequest({
        ...request,
        documentData: response.data.data
      });
      setShowDocument(true);
    } catch (error) {
      console.error('Error fetching document:', error);
    }
  };

  const handleDelete = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      await deleteRequest(requestId);
    }
  };

  const getStatusBadge = (status) => {
    const baseClass = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case 'pending':
        return `${baseClass} bg-yellow-100 text-yellow-800 border border-yellow-200`;
      case 'admin_approved':
      case 'approved':
        return `${baseClass} bg-blue-100 text-blue-800 border border-blue-200`;
      case 'waiting_noc':
        return `${baseClass} bg-indigo-100 text-indigo-800 border border-indigo-200`;
      case 'completed':
        return `${baseClass} bg-green-100 text-green-800 border border-green-200`;
      case 'rejected':
        return `${baseClass} bg-red-100 text-red-800 border border-red-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 border border-gray-200`;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Review',
      'admin_approved': 'Admin Approved',
      'approved': 'Approved',
      'waiting_noc': 'Awaiting NOC',
      'completed': 'Completed',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  const canDelete = (request) => {
    return user?.role === 'mitra' && request.status === 'pending';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const requests = data?.data?.requests || data?.requests || [];
  const pagination = data?.data?.pagination || data?.pagination || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Account ID or Order ID..."
              className="form-input pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              className="form-input w-auto"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No upgrade requests found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bandwidth
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <React.Fragment key={request.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Account: {request.account_id}
                          </p>
                          {request.order_id && (
                            <p className="text-sm text-gray-500">
                              Order: {request.order_id}
                            </p>
                          )}
                          <p className="text-sm text-gray-500">
                            {request.service_type}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className="font-medium">{request.current_bandwidth}</span>
                          <span className="text-gray-500 mx-2">→</span>
                          <span className="font-medium text-green-600">{request.requested_bandwidth}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(request.status)}>
                          {getStatusLabel(request.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(request.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setShowDetails(showDetails === request.id ? null : request.id)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          
                          {request.status === 'approved' && (
                            <button
                              onClick={() => handleViewDocument(request)}
                              className="text-green-600 hover:text-green-900"
                              title="View Acceptance Document"
                            >
                              <DocumentArrowDownIcon className="h-5 w-5" />
                            </button>
                          )}
                          
                          {canDelete(request) && (
                            <button
                              onClick={() => handleDelete(request.id)}
                              disabled={deleteLoading}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {showDetails === request.id && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Justification</h4>
                              <p className="text-gray-700 whitespace-pre-wrap">{request.justification}</p>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Addresses</h4>
                              <div className="space-y-2">
                                <div>
                                  <span className="font-medium">Site A:</span>
                                  <p className="text-gray-700">{request.site_a_address}</p>
                                </div>
                                <div>
                                  <span className="font-medium">Site B:</span>
                                  <p className="text-gray-700">{request.site_b_address}</p>
                                </div>
                              </div>
                            </div>
                            {request.admin_comments && (
                              <div className="md:col-span-2">
                                <h4 className="font-medium text-gray-900 mb-2">Admin Comments</h4>
                                <p className="text-gray-700 bg-yellow-50 p-3 rounded">{request.admin_comments}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                disabled={currentPage === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * 10) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 10, pagination.total)}
                  </span> of{' '}
                  <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                    disabled={currentPage === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDocument && selectedRequest && (
        <AcceptanceDocument
          request={selectedRequest}
          onClose={() => {
            setShowDocument(false);
            setSelectedRequest(null);
          }}
        />
      )}
    </div>
  );
};

export default RequestList;